import { FormControl, FormGroup, FormArray } from "@angular/forms";
import { DeviceBase } from "./device-base";
import { PropertyMetadata } from "../../properties/property-types";
import { RegisterElement } from "../element-decorator";
import { ParkingLevelElement } from "../parking/parkinglevel";
import { LevelDesignerElementBase, ElementValidationResult } from "../element-base";

@RegisterElement()
export class Sign extends DeviceBase {
    protected sizeInMeters: number = 1;
    public override TypeName: string = "Sign"
    public override ParentType: string | null = null;
    public override ParentFormGroupName: string | null = "SignConfigurations";

    constructor(formGroup: FormGroup) {
        super(formGroup, 'assets/icons/sign.svg');
        const idValue = formGroup.get('Id')?.value;
        if (idValue && typeof idValue !== 'number') {
            formGroup.get('Id')?.setValue(null);
        }
        // Ensure required form controls exist
        if (!formGroup.contains('Name')) {
            formGroup.addControl('Name', new FormControl(''));
        }
        if (!formGroup.contains('Mode')) {
            formGroup.addControl('Mode', new FormControl(0));
        }
        if (!formGroup.contains('DayBrightness')) {
            formGroup.addControl('DayBrightness', new FormControl(0));
        }
        if (!formGroup.contains('NightBrightness')) {
            formGroup.addControl('NightBrightness', new FormControl(0));
        }
        if (!formGroup.contains('DisplayRuleSetId')) {
            formGroup.addControl('DisplayRuleSetId', new FormControl(null));
        }
        if (!formGroup.contains('LocalAddress')) {
            formGroup.addControl('LocalAddress', new FormControl(''));
        }
        if (!formGroup.contains('LocalPort')) {
            formGroup.addControl('LocalPort', new FormControl(0));
        }
        if (!formGroup.contains('LocalControllerId')) {
            formGroup.addControl('LocalControllerId', new FormControl(null));
        }
        if (!formGroup.contains('ChipId')) {
            formGroup.addControl('ChipId', new FormControl(null));
        }
    }

    public override clone(): Sign {
        return new Sign(this.formGroup);
    }

    public override getPropertyMetadata(): PropertyMetadata[] {
        return [
            ...super.getPropertyMetadata(),
            {
                formControlName: 'ChipId',
                displayName: 'Chip ID',
                inputType: { 
                    type: 'searchable-select',
                    url: '/hardware/signs/available/searches',
                    idProperty: 'ChipId',
                    labelProperty: 'ChipId',
                    minSearchLength: 2
                },
                order: 5,
                category: 'Hardware',
                hideOnMultipleSelection: true,
            },
            {
                formControlName: 'Mode',
                displayName: 'Mode',
                inputType: { 
                    type: 'select',
                    options: [
                        { value: '0', label: 'Polling' },
                        { value: '1', label: 'Local Device' },
                        { value: '2', label: 'Polling Master' },
                        { value: '3', label: 'Local Device Master' },
                        { value: '4', label: 'Slave' },
                        { value: '5', label: 'Push' },
                        { value: '6', label: 'Reserved' }
                    ]
                },
                order: 7,
                category: 'Hardware'
            },
            {
                formControlName: 'DayBrightness',
                displayName: 'Day Brightness',
                inputType: { 
                    type: 'number',
                    min: 0,
                    max: 100
                },
                order: 8,
                category: 'Display'
            },
            {
                formControlName: 'NightBrightness',
                displayName: 'Night Brightness', 
                inputType: {
                    type: 'number',
                    min: 0,
                    max: 100
                },
                order: 9,
                category: 'Display'
            },
            {
                formControlName: 'DisplayRuleSetId',
                displayName: 'Display Rule Set',
                inputType: {
                    type: 'searchable-select',
                    url: '/infrastructure/displayrulesets/searches',
                    idProperty: 'Id',
                    labelProperty: 'Name',
                    minSearchLength: 2
                },
                order: 10,
                category: 'Display'
            },
            {
                formControlName: 'LocalControllerId',
                displayName: 'Controller',
                inputType: { 
                    type: 'form-control-select',
                    formArrayName: 'Controllers',
                    idProperty: 'Id',
                    labelProperty: 'Name'
                },
                description: 'Controller assigned to this sign',
                order: 5,
                category: 'Hardware'
            },
            {
                formControlName: 'LocalAddress',
                displayName: 'Local Address',
                inputType: { type: 'text' },
                description: 'Local network address for the sign',
                order: 6,
                category: 'Hardware'
            },
            {
                formControlName: 'LocalPort',
                displayName: 'Local Port',
                inputType: { 
                    type: 'number',
                    min: 0,
                    max: 65535
                },
                description: 'Local network port for the sign',
                order: 7,
                category: 'Hardware'
            },
            // {
            //     formControlName: 'WakeUpTime',
            //     displayName: 'Wake Up Time',
            //     inputType: { type: 'time' },
            //     order: 11,
            //     category: 'Schedule'
            // },
            // {
            //     formControlName: 'SleepTime',
            //     displayName: 'Sleep Time',
            //     inputType: { type: 'time' },
            //     order: 12,
            //     category: 'Schedule'
            // },
            // {
            //     formControlName: 'StartOfDayTime',
            //     displayName: 'Start of Day',
            //     inputType: { type: 'time' },
            //     order: 13,
            //     category: 'Schedule'
            // },
            // {
            //     formControlName: 'EndOfDayTime',
            //     displayName: 'End of Day',
            //     inputType: { type: 'time' },
            //     order: 14,
            //     category: 'Schedule'
            // },
        ];
    }

    public override default(): Sign {
        return this;
    }

    public override checkPositionValid(elements: LevelDesignerElementBase[]): ElementValidationResult {
        const result = super.checkPositionValid(elements);
        
        // Find the level element
        const levelElement = elements.find(e => e instanceof ParkingLevelElement);
        if (levelElement && !this.currentParentElement && this.ParentFormGroupName) {
            // Add to level's SignConfigurations form array if not already there
            const signConfigurations = levelElement.formGroup.get(this.ParentFormGroupName) as FormArray;
            if (signConfigurations && !signConfigurations.controls.includes(this.formGroup)) {
                signConfigurations.push(this.formGroup);
                this.currentParentElement = levelElement;
            }
        }

        return result;
    }
    override checkPropertiesValid(allElements: LevelDesignerElementBase[]): ElementValidationResult {
        const result = super.checkPropertiesValid(allElements);

        // Check if Mode is null
        const modeControl = this.formGroup.get('Mode');
        if (modeControl?.value == null) {
            result.errors.push('Sign must have a Mode selected');
        }

        // Check if DisplayRuleSetId is null
        const displayRuleSetIdControl = this.formGroup.get('DisplayRuleSetId');
        if (displayRuleSetIdControl?.value == null) {
            result.errors.push('Sign must have a Display Rule Set selected');
        }

        return result;
    }
} 
